import React, { useMemo } from 'react'
import { DashboardWidgetType } from 'features/dashboard/types'
import { getLabel } from 'utils/widgetTypeUtil'
import { useDisplayTheme, useSysTheme } from 'hooks/useUiState'
import { DayHourTimeZone, TimeInterval } from 'enums'
import messageUtil from 'utils/messageUtil'
import { getContentType } from 'utils/analysisTypeUtil'
import { getYearDateHourMinuteDisplay } from 'utils/timeZoneUtil'
import { usePageConfigUser } from 'hooks/usePageConfig'
import { useTranslation } from 'react-i18next'
import { InsightViewOptionStrings } from 'enums/InsightViewOption'
import { sendTypedMessage } from 'utils/iframeUtil'
import {
  StyledInfoContentContainer,
  Label,
  Value,
  LinkValue,
  Divider,
  ValueList,
  ValueListItem
} from './index.css'
import {
  getThemeInfos,
  isAudienceUsageAndAttitudeWidget,
  isCrossTabWidget,
  isCrossTabWidgetOrSideBySideWidget,
  isDayHourWidget,
  isLanguageWidget,
  isSourcesWidget,
  isStreamWidget,
  isTimelineComparisonWidget,
  isTimelineWidget,
  isWordCloudWidget,
  getDisplayTimeZone
} from '../utils'

interface InfoContentProps {
  widgetSetting: DashboardWidgetType & {
    viewOption?: InsightViewOptionStrings
    topN?: number
    minCount?: number
  }
}

interface InfoRowProps {
  label: React.ReactNode
  value: React.ReactNode
}

const InfoRow: React.FC<InfoRowProps> = ({ label, value }) => {
  const theme = useSysTheme()
  return (
    <>
      <Label $theme={theme}>{label}</Label>
      <Value $theme={theme}>{value}</Value>
    </>
  )
}

const TopicInfo = ({ widgetSetting }: InfoContentProps) => {
  const mode = useDisplayTheme()
  const theme = useSysTheme()
  const { t } = useTranslation()

  return (
    <>
      {widgetSetting.topicInfos && widgetSetting.topicInfos.length > 0 && (
        <>
          <Label $theme={theme}>{t('topic_other')}</Label>
          <ValueList>
            {widgetSetting.topicInfos.flatMap((topic) => (
              <ValueListItem key={topic.topicId}>
                {topic.accessible ? (
                  <LinkValue
                    $mode={mode}
                    $theme={theme}
                    onClick={() => sendTypedMessage('goToTopic', topic.topicId)}>
                    {topic.name}
                  </LinkValue>
                ) : (
                  <Value $theme={theme}>
                    {topic.name} ({t('inaccessible')})
                  </Value>
                )}
              </ValueListItem>
            ))}
          </ValueList>
        </>
      )}
    </>
  )
}

const ThemeInfo = ({ widgetSetting }: InfoContentProps) => {
  const mode = useDisplayTheme()
  const theme = useSysTheme()
  const { t } = useTranslation()
  const themeInfos = useMemo(() => getThemeInfos(widgetSetting), [widgetSetting])

  return (
    <>
      {themeInfos!.length > 0 && (
        <>
          <Label $theme={theme}>{t('theme_other')}</Label>
          <ValueList>
            {themeInfos?.map((themeInfo) => (
              <ValueListItem key={themeInfo.themeId}>
                <LinkValue
                  $mode={mode}
                  $theme={theme}
                  onClick={() => sendTypedMessage('goToTheme', themeInfo?.themeId)}>
                  {themeInfo?.name}
                </LinkValue>
              </ValueListItem>
            ))}
          </ValueList>
        </>
      )}
    </>
  )
}

const InfoContent = ({ widgetSetting }: InfoContentProps) => {
  const mode = useDisplayTheme()
  const theme = useSysTheme()
  const userConfig = usePageConfigUser()
  const { t } = useTranslation()
  const themeInfos = getThemeInfos(widgetSetting)

  const renderDateRange = () => (
    <>
      {widgetSetting.timeInterval === TimeInterval.CUSTOM ? (
        <>
          {getYearDateHourMinuteDisplay(widgetSetting.startDatetime!, userConfig.timeZone)} -{' '}
          {getYearDateHourMinuteDisplay(widgetSetting.endDatetime!, userConfig.timeZone)}
        </>
      ) : (
        <>{t(`timeInterval.${widgetSetting.timeInterval}`)}</>
      )}
    </>
  )

  const commonFields = [
    { label: t('widget'), value: getLabel(widgetSetting.type) },
    { label: t('type'), value: t(`${getContentType(widgetSetting.analysisType!)}`) },
    { label: t('dateRange'), value: renderDateRange() }
  ]

  const conditionalFields = [
    // View option
    widgetSetting.viewOption &&
      !isLanguageWidget(widgetSetting) &&
      !isSourcesWidget(widgetSetting) && {
        label: t('view'),
        value: messageUtil.viewOption(widgetSetting.viewOption)
      },

    // Top N
    widgetSetting.topN && { label: t('items'), value: `${t('top')} ${widgetSetting.topN}` },

    // Stream widget sort
    isStreamWidget(widgetSetting) &&
      widgetSetting.sort && {
        label: t('sort'),
        value: t(`streamSortByGroup.${widgetSetting.sort}`)
      },

    // Word cloud settings
    isWordCloudWidget(widgetSetting) &&
      widgetSetting.font && {
        label: t('font'),
        value: t(`wordCloudfontGroup.${widgetSetting.font}`)
      },

    isWordCloudWidget(widgetSetting) &&
      widgetSetting.color && {
        label: t('color'),
        value: t(`wordCloudColorGroup.${widgetSetting.color}`)
      },

    // Timeline widget
    isTimelineWidget(widgetSetting) &&
      widgetSetting.timeUnit && {
        label: t('timeUnit'),
        value: t(`timeUnitGroup.${widgetSetting.timeUnit}`)
      },

    // Usage And Attitude widget
    isAudienceUsageAndAttitudeWidget(widgetSetting) &&
      widgetSetting.topNSecond && {
        label: `${t('secondlevel')} ${t('items')}`,
        value: `${t('top')} ${widgetSetting.topNSecond}`
      },

    // Day hour widget
    isDayHourWidget(widgetSetting) && {
      label: t('timeZone'),
      value:
        widgetSetting.timeZoneOption === DayHourTimeZone.GMT
          ? getDisplayTimeZone(userConfig.timeZone)
          : t('authorLocalTimeZone')
    },

    // Cross tab widget
    isCrossTabWidgetOrSideBySideWidget(widgetSetting) && {
      label: t('displaying'),
      value: widgetSetting.rowComparisonMode
        ? `${t(`comparisonModeOption.${widgetSetting.rowComparisonMode}`)} vs. ${t(
            `comparisonModeOption.${widgetSetting.colComparisonMode}`
          )}`
        : `${t('comparisonModeOption.TOPICS')} vs. ${t(
            `comparisonModeOption.${widgetSetting.colComparisonMode}`
          )}`
    },

    isCrossTabWidget(widgetSetting) &&
      widgetSetting.ctSort && {
        label: t('sort'),
        value: t(`crosstabSortGroup.${widgetSetting.ctSort}`)
      },

    // Timeline comparison widget
    isTimelineComparisonWidget(widgetSetting) &&
      widgetSetting.comparisonMode && {
        label: t('displaying'),
        value: t(`comparisonModeOption.${widgetSetting.comparisonMode}`)
      },

    // Min count
    widgetSetting.minCount && { label: t('hideCountsBelow'), value: widgetSetting.minCount }
  ].filter(Boolean) as { label: string; value: React.ReactNode }[]

  const fieldRows = [...commonFields, ...conditionalFields]

  const hasTopics = widgetSetting.topicInfos && widgetSetting.topicInfos.length > 0
  const hasThemes = themeInfos && themeInfos.length > 0

  return (
    <StyledInfoContentContainer $theme={theme}>
      {fieldRows.map((field, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <InfoRow key={index} label={field.label} value={field.value} />
      ))}
      {(hasTopics || hasThemes) && <Divider $mode={mode} />}
      {hasTopics && <TopicInfo widgetSetting={widgetSetting} />}
      {hasThemes && <ThemeInfo widgetSetting={widgetSetting} />}
    </StyledInfoContentContainer>
  )
}

export default InfoContent
